import { useState, useRef } from "react";
import { useTenant } from "@/contexts/TenantContext";
import { useURLParams } from "@/contexts/URLParamsContext";
import { useIsAuthenticated, useAuthLoading } from "@/hooks/auth/useAuth";
import { Property } from "@/types/search";
import { useSearchQuery } from "@/hooks/search/useSearchQuery";
import { getContrastText } from "@/utils/colorUtils";
import { buildPropertyUrl } from "@/utils/propertyUrl";
import { LandingSearchBox } from "@/components/features/search/LandingSearchBox";
import { resolveSearchCity } from "@/utils/searchTransitionUtils";
import SearchResults from "@/components/features/search/SearchResults";
import { SqueezePageOverlay } from "@/components/auth/SqueezePageOverlay";
import { ResponsiveContainer } from "@/components/shared/responsive";
import FeatureCards from "@/components/features/landing-page/FeatureCards";
import { SuggestedProperties } from "@/components/features/home/SuggestedProperties";
import { AILoadingText } from "@/components/common/AILoadingText";
import { Search } from "lucide-react";
import { TeamMember } from "@/api/teamMembers";
import TeamMemberHighlights from "@/components/features/team/TeamMemberHighlights";

interface LandingPageProps {
  teamMember?: TeamMember;
}

const LandingPage: React.FC<LandingPageProps> = ({ teamMember }) => {
  const { config } = useTenant();
  const { params: urlParams } = useURLParams();
  const isAuthenticated = useIsAuthenticated();
  const authLoading = useAuthLoading();
  const cityState = [
    config?.orgData?.client_address?.city,
    config?.orgData?.client_address?.state,
  ]
    .filter(Boolean)
    .join(", ");
  const [searchQuery, setSearchQuery] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  // Squeeze page: shown to non-authenticated email-campaign visitors
  // (contact_id + utm_source=email), until they dismiss it.
  const [squeezeDismissed, setSqueezeDismissed] = useState(false);
  const showSqueezePage =
    !authLoading &&
    !isAuthenticated &&
    !!urlParams.contact_id &&
    urlParams.utm_source === "email" &&
    !squeezeDismissed;

  const handleSqueezePageClose = () => {
    setSqueezeDismissed(true);
  };

  const handleSqueezePageSuccess = () => {
    setSqueezeDismissed(true);
  };

  const {
    results,
    loading,
    error,
    hasMore,
    search,
    loadMore,
    filters,
    totalAvailable = 0,
    retryFailedRequest,
    timestamp,
  } = useSearchQuery();

  const handlePropertyClick = (property: Property) => {
    window.open(buildPropertyUrl(property, config?.id || ""), "_blank");
  };

  const hasResults = results.length > 0;

  // Once a search returns results, the page stays in its results layout for
  // the rest of the session. Latched here rather than via an effect; the
  // re-render is already driven by `results` changing.
  const hadSuccessfulSearchRef = useRef(false);
  if (hasResults) hadSuccessfulSearchRef.current = true;
  const hadSuccessfulSearch = hadSuccessfulSearchRef.current;

  // Get tenant colors for styling
  const primaryColor = config?.branding?.colors?.primary || "#2563eb";
  const textColor = getContrastText(primaryColor);

  return (
    <>
      <div className={`min-h-screen ${showSqueezePage ? "blur-sm" : ""}`}>
        <div
          className={`relative transition-all duration-500 ${
            hadSuccessfulSearch
              ? "py-5 xl:py-10 3xl:py-20"
              : "py-10 xl:py-14 3xl:py-28"
          }`}
          style={{
            background: `
            radial-gradient(circle at 0% 0%, 
              ${primaryColor}10 0%, 
              transparent 50%
            ),
            radial-gradient(circle at 100% 100%,
              ${primaryColor}20 0%,
              transparent 50%
            ),
            linear-gradient(
              135deg,
              ${primaryColor}99 0%,
              ${primaryColor}cc 40%,
              ${primaryColor}ee 70%,
              ${primaryColor}ff 100%
            )
          `,
          }}
        >
          <ResponsiveContainer>
            <div className="hero-content">
              <div className={`transition-all duration-500 `}>
                <div className="text-center">
                  <h1
                    className={`animate-fade-up transition-all duration-500 ${
                      hadSuccessfulSearch
                        ? "heading-responsive-1"
                        : "heading-hero"
                    }`}
                    style={{ color: textColor }}
                  >
                    <span className="block md:inline text-4xl xl:text-5xl 3xl:text-6xl font-bold">
                      Discover Your
                    </span>{" "}
                    <span className="block md:inline text-4xl xl:text-5xl 3xl:text-6xl font-bold">
                      Perfect Property
                    </span>
                  </h1>
                  {!hadSuccessfulSearch && (
                    <>
                      <p
                        className="mt-3  sm:w-[500px] xl:w-[700px] 3xl:w-[950px] mx-auto xl:mt-4 hero-subtitle animate-fade-in-delay text-sm xl:text-lg 3xl:text-2xl font-normal 3xl:!leading-10"
                        style={{ color: textColor }}
                      >
                        Use our AI-powered search to find your dream property.
                        Describe what you're looking for in{" "}
                        <em>natural language</em>, just like talking to a real
                        estate agent.
                      </p>
                    </>
                  )}
                </div>

                <div
                  className={`mt-6 xl:mt-10 max-w-xl xl:max-w-2xl 3xl:max-w-4xl mx-auto animate-fade-up ${
                    hadSuccessfulSearch
                      ? "lg:max-w-4xl xl:max-w-4xl 3xl:max-w-5xl"
                      : ""
                  }`}
                >
                  <LandingSearchBox
                    onSearch={(filters) => {
                      const [city, state] = cityState.split(", ");
                      setHasSearched(true);
                      search({
                        ...filters,
                        tenant_id: config?.id || "",
                        location: {
                          city: resolveSearchCity(filters, city || ""),
                          state: state || "",
                        },
                      });
                    }}
                    initialFilters={{ ...filters, query: searchQuery }}
                    isLoading={loading}
                    onQueryChange={setSearchQuery}
                    showFilters={hadSuccessfulSearch}
                    filterTextColor={textColor}
                    hasSearched={hasSearched} // Pass hasSearched state to LandingSearchBox
                  />

                  {loading && !hadSuccessfulSearch && (
                    <div className="mt-3 lg:mt-5">
                      <AILoadingText
                        style={{ color: textColor }}
                        isHomePage={true}
                      />
                    </div>
                  )}
                  {hasSearched &&
                    results.length === 0 &&
                    !hadSuccessfulSearch &&
                    !loading && (
                      <div className="text-center mt-3 lg:mt-8 animate-fade-in">
                        <p
                          className="text-sm xl:text-lg font-medium"
                          style={{ color: textColor }}
                        >
                          No properties found matching your search
                        </p>
                        <p
                          className="mt-1 text-xs xl:text-base flex items-center justify-center gap-1"
                          style={{ color: textColor }}
                        >
                          Please try different search terms{" "}
                          <Search
                            className="w-3 h-3 xl:w-4 xl:h-4"
                            style={{ color: textColor }}
                          />
                        </p>
                      </div>
                    )}
                </div>
              </div>
            </div>
          </ResponsiveContainer>

          {/* Team/Website Name - shown as hero footer when "Show on home page" is enabled */}
          {config?.branding?.teamName && !hadSuccessfulSearch && (
            <div className="absolute bottom-0 left-0 right-0 pb-4 xl:pb-6">
              <p
                className="text-center text-sm xl:text-base tracking-wide font-light animate-fade-up"
                style={{ color: textColor, opacity: 0.8 }}
              >
                {config.branding.teamName}
              </p>
            </div>
          )}
        </div>

        {hadSuccessfulSearch && (
          <div className="py-12 p-6 lg:p-10 max-w-[1400px] mx-auto">
            <SearchResults
              results={results}
              loading={loading}
              error={error}
              hasMore={hasMore}
              onLoadMore={loadMore}
              onPropertyClick={handlePropertyClick}
              totalAvailable={totalAvailable}
              onRetry={retryFailedRequest}
              filters={filters?.filters}
              searchQuery={filters?.query}
              timestamp={timestamp}
            />
          </div>
        )}

        {!hadSuccessfulSearch && (
          <>
            {/* Team Member Highlights Section - Only shown if teamMember prop is provided */}
            {teamMember && (
              <div className="py-12 3xl:py-12  bg-gray-100">
                <ResponsiveContainer>
                  <TeamMemberHighlights
                    teamMember={teamMember}
                    primaryColor={primaryColor}
                  />
                </ResponsiveContainer>
              </div>
            )}

            {/* Featured Properties Section */}
            <div className="py-12 3xl:py-16 ">
              <div className=" w-full mx-auto max-w-[1400px] px-4 xl:px-6 py-6">
                <SuggestedProperties cityState={cityState} />
              </div>
            </div>
          </>
        )}

        <div className="py-6 lg:py-10 bg-gray-100">
          <div className=" w-full mx-auto max-w-[1400px]  px-4 xl:px-6 py-6">
            <FeatureCards />
          </div>
        </div>
      </div>

      {/* Squeeze page overlay */}
      {showSqueezePage && (
        <SqueezePageOverlay
          onClose={handleSqueezePageClose}
          onSuccess={handleSqueezePageSuccess}
          isConfirmationMode={
            !!urlParams.contact_id && urlParams.utm_source === "email"
          }
        />
      )}
    </>
  );
};

export default LandingPage;
